import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import StatusBadge from "@/components/StatusBadge";
import usePageTitle from "@/hooks/use-page-title";
import { ShoppingBag, Package, User as UserIcon, MapPin, Plus, Trash2, Star, Save } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, fmtDateTime } from "@/lib/date";

const downloadInvoice = async (shopOrderId, orderNo) => {
  try {
    const res = await api.get(`/shop/orders/${shopOrderId}/invoice.pdf`, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Invoice-${orderNo || shopOrderId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(e?.response?.status === 400 ? "Invoice available only after payment" : "Could not download invoice");
  }
};

export default function CustomerPortal() {
  usePageTitle("My Portal");
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [shopOrders, setShopOrders] = useState([]);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
    api.get("/payments").then((r) => setPayments(r.data)).catch(() => setPayments([]));
    api.get("/shop/my-orders").then((r) => setShopOrders(r.data)).catch(() => setShopOrders([]));
  }, []);

  const outstanding = orders.reduce((s, o) => s + (o.balance || 0), 0);
  const totalOrders = orders.length;
  const delivered = orders.filter((o) => ["delivered", "completed"].includes(o.status)).length;

  return (
    <div className="space-y-8" data-testid="customer-portal-root">
      <div
        className="relative overflow-hidden border border-[hsl(var(--border))]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.75)), url('https://images.unsplash.com/photo-1646153114001-495dfb56506d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxjcmVhdGl2ZSUyMGFkdmVydGlzaW5nJTIwYWdlbmN5JTIwb2ZmaWNlfGVufDB8fHx8MTc4Mjg5MzAxMnww&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="p-10 lg:p-14 text-white">
          <div className="overline text-white/60 mb-3">Customer Portal</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mb-2">
            Welcome back, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-white/70 max-w-lg">
            Track your orders, approve designs, view invoices and download files — all in one place.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          ["Total Orders", totalOrders],
          ["Delivered", delivered],
          ["Outstanding", `₹${Math.round(outstanding).toLocaleString("en-IN")}`],
        ].map(([k, v]) => (
          <div key={k} className="card-swiss p-6">
            <div className="overline">{k}</div>
            <div className="font-display font-black text-3xl mt-2">{v}</div>
          </div>
        ))}
      </div>

      <div className="card-swiss">
        <div className="p-5 border-b border-[hsl(var(--border))] overline">Your Orders</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[hsl(var(--border))]">
              <th className="px-4 py-3 text-left overline">Order</th>
              <th className="px-4 py-3 text-left overline">Product</th>
              <th className="px-4 py-3 text-right overline">Amount</th>
              <th className="px-4 py-3 text-right overline">Balance</th>
              <th className="px-4 py-3 text-left overline">Status</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]">
                <td className="px-4 py-3">
                  <Link to={`/track/${o.id}`} className="font-mono text-xs hover:text-[#FF6B00]">
                    {o.id}
                  </Link>
                </td>
                <td className="px-4 py-3">{o.product_name}</td>
                <td className="px-4 py-3 text-right font-mono">
                  ₹{Math.round(o.estimated_cost || 0).toLocaleString("en-IN")}
                </td>
                <td className="px-4 py-3 text-right font-mono text-[#FF6B00]">
                  ₹{Math.round(o.balance || 0).toLocaleString("en-IN")}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={o.status} />
                </td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">
            You don&apos;t have any orders yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="card-swiss">
        <div className="p-5 border-b border-[hsl(var(--border))] overline flex items-center gap-2">
          <ShoppingBag size={14} className="text-[#FF6B00]" /> Online Shop Orders
        </div>
        {shopOrders.length === 0 ? (
          <div className="px-4 py-12 text-center text-muted-foreground">
            No online orders yet. <Link to="/products" className="text-[#FF6B00] font-bold hover:underline">Browse products</Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[hsl(var(--border))]">
                <th className="px-4 py-3 text-left overline">Order</th>
                <th className="px-4 py-3 text-left overline">Items</th>
                <th className="px-4 py-3 text-right overline">Total</th>
                <th className="px-4 py-3 text-left overline">Payment</th>
                <th className="px-4 py-3 text-left overline">Status</th>
              </tr>
            </thead>
            <tbody data-testid="customer-shop-orders">
              {shopOrders.map((o) => (
                <tr key={o.id} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]">
                  <td className="px-4 py-3">
                    <Link to={`/order-confirmation/${o.id}`} className="font-mono text-xs hover:text-[#FF6B00] font-bold">
                      {o.order_no}
                    </Link>
                    <div className="text-[10px] text-muted-foreground">{fmtDate(o.created_at)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Package size={14} className="text-zinc-400" />
                      <div>
                        <div className="text-sm">{o.items?.length || 0} {(o.items?.length || 0) === 1 ? "item" : "items"}</div>
                        <div className="text-[10px] text-muted-foreground truncate max-w-xs">{o.items?.[0]?.name}{o.items?.length > 1 ? ` + ${o.items.length - 1} more` : ""}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">₹{Math.round(o.total).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${o.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {(o.payment_status || "pending").toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-bold text-[#FF6B00] capitalize">{(o.status || "").replace(/_/g, " ")}</span>
                    {o.payment_status === "paid" && (
                      <button
                        type="button"
                        onClick={() => downloadInvoice(o.id, o.order_no)}
                        className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-zinc-600 hover:text-[#FF6B00] underline"
                        data-testid={`invoice-download-${o.id}`}
                      >
                        📄 Invoice
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card-swiss">
        <div className="p-5 border-b border-[hsl(var(--border))] overline">Payment History</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[hsl(var(--border))]">
              <th className="px-4 py-3 text-left overline">Date</th>
              <th className="px-4 py-3 text-left overline">Order</th>
              <th className="px-4 py-3 text-left overline">Mode</th>
              <th className="px-4 py-3 text-right overline">Amount</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} className="border-b border-[hsl(var(--border))]">
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                  {fmtDate(p.created_at)}
                </td>
                <td className="px-4 py-3 font-mono text-xs">{p.order_id}</td>
                <td className="px-4 py-3 uppercase text-xs">{p.mode}</td>
                <td className="px-4 py-3 text-right font-mono">
                  ₹{Math.round(p.amount).toLocaleString("en-IN")}
                </td>
              </tr>
            ))}
            {payments.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-12 text-center text-muted-foreground">
                  No payments yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ProfileSection />
      <AddressBookSection />
    </div>
  );
}

// ---------- Profile ----------
function ProfileSection() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (user) setForm({ name: user.name || "", phone: user.phone || "", email: user.email || "" });
  }, [user]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/me/profile", form);
      toast.success("Profile updated");
      // Refresh the auth user so headers / greetings update immediately
      try {
        const r = await api.get("/auth/me");
        if (setUser && r.data) setUser(r.data);
      } catch { /* non-fatal */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="card-swiss" data-testid="portal-profile">
      <div className="p-5 border-b border-[hsl(var(--border))] overline flex items-center gap-2"><UserIcon size={12} /> Profile</div>
      <form onSubmit={save} className="p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="input" data-testid="profile-name" />
        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="input" data-testid="profile-phone" />
        <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="input" data-testid="profile-email" />
        <div className="md:col-span-3">
          <button type="submit" disabled={busy} className="btn-swiss btn-accent disabled:opacity-50" data-testid="profile-save">
            <Save size={12} /> {busy ? "Saving…" : "Save Profile"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------- Address Book ----------
const EMPTY_ADDR = { full_name: "", phone: "", email: "", line1: "", line2: "", city: "", state: "", pincode: "", gstin: "", is_default: false };
function AddressBookSection() {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_ADDR);
  const [editId, setEditId] = useState("");
  const load = () => api.get("/me/addresses").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await api.put(`/me/addresses/${editId}`, form);
        toast.success("Address updated");
      } else {
        await api.post("/me/addresses", form);
        toast.success("Address added");
      }
      setShowForm(false); setForm(EMPTY_ADDR); setEditId("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this address?")) return;
    try { await api.delete(`/me/addresses/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };
  const markDefault = async (id) => {
    try { await api.put(`/me/addresses/${id}/default`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="card-swiss" data-testid="portal-addresses">
      <div className="p-5 border-b border-[hsl(var(--border))] overline flex items-center justify-between">
        <span className="inline-flex items-center gap-2"><MapPin size={12} /> Address Book</span>
        <button onClick={() => { setForm(EMPTY_ADDR); setEditId(""); setShowForm(!showForm); }} className="text-xs text-[#FF6B00] font-bold" data-testid="address-add-toggle">
          {showForm ? "Cancel" : "+ Add address"}
        </button>
      </div>
      {showForm && (
        <form onSubmit={save} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3 border-b border-[hsl(var(--border))]">
          <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Full name" className="input" data-testid="addr-full-name" />
          <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="input" data-testid="addr-phone" />
          <input required value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} placeholder="Address line 1" className="input md:col-span-2" data-testid="addr-line1" />
          <input value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })} placeholder="Address line 2 (optional)" className="input md:col-span-2" data-testid="addr-line2" />
          <input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="input" data-testid="addr-city" />
          <input required value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="State" className="input" data-testid="addr-state" />
          <input required value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} placeholder="Pincode" className="input" data-testid="addr-pincode" />
          <input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} placeholder="GSTIN (optional)" className="input" data-testid="addr-gstin" />
          <label className="inline-flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} data-testid="addr-default" />
            Set as default
          </label>
          <div className="md:col-span-2">
            <button className="btn-swiss btn-accent" data-testid="addr-save"><Save size={12} /> {editId ? "Update Address" : "Save Address"}</button>
          </div>
        </form>
      )}
      <div className="p-5 space-y-3">
        {rows.length === 0 && !showForm && (
          <div className="text-sm text-muted-foreground text-center py-6">No saved addresses yet — click "Add address" to add one.</div>
        )}
        {rows.map((a) => (
          <div key={a.id} className="border border-[hsl(var(--border))] rounded p-4 flex items-start justify-between gap-3" data-testid={`addr-row-${a.id}`}>
            <div className="text-sm">
              <div className="font-bold">
                {a.full_name}
                {a.is_default && <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-emerald-600 border border-emerald-300 bg-emerald-50 px-1.5 rounded">Default</span>}
              </div>
              <div className="text-muted-foreground text-xs mt-1">
                {a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} — {a.pincode}
                {a.phone && <><br />📞 {a.phone}</>}
                {a.gstin && <><br />GSTIN: {a.gstin}</>}
              </div>
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              {!a.is_default && (
                <button onClick={() => markDefault(a.id)} className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-[#FF6B00]" data-testid={`addr-default-${a.id}`}>
                  <Star size={11} className="inline" /> Set default
                </button>
              )}
              <button onClick={() => { setForm({ ...EMPTY_ADDR, ...a }); setEditId(a.id); setShowForm(true); }} className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-[#FF6B00]" data-testid={`addr-edit-${a.id}`}>
                Edit
              </button>
              <button onClick={() => del(a.id)} className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-red-600" data-testid={`addr-delete-${a.id}`}>
                <Trash2 size={11} className="inline" /> Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
